import {
  cellKey,
  countCardsInColumn,
  countCardsInRow,
  countCardsInSwimlane,
  createCard,
  createColumn,
  createGroup,
  createRow,
  createSwimlane,
  deleteColumn,
  deleteGroup,
  deleteRow,
  deleteSwimlane,
  moveColumn,
  moveRow,
  moveRowToSwimlane,
  moveSwimlane,
  renameColumn,
  renameGroup,
  renameRow,
  renameSwimlane,
  setColumnGroup,
  type Direction,
} from '@blueprint/domain'
import { useMemo } from 'react'
import { useBoardStore } from '../../state/board-store.js'
import { useUiStore } from '../../state/ui-store.js'

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`
}

/**
 * Structure handlers. They are created once and shared, so the top-level component
 * composes features instead of implementing every board handler, and card previews keep
 * stable callback identities.
 */
export function useStoryMapActions() {
  return useMemo(() => {
    const run = (...args: Parameters<ReturnType<typeof useBoardStore.getState>['run']>) =>
      useBoardStore.getState().run(...args)
    const confirmThen = (
      title: string,
      message: string,
      consequence: string | undefined,
      onConfirm: () => void,
    ) => {
      useUiStore.getState().requestConfirm({
        title,
        message,
        consequence,
        confirmLabel: 'Delete',
        undoable: true,
        onConfirm,
      })
    }

    return {
      addColumn: (beforeId: string | null = null) =>
        run((state) => createColumn(state, { title: 'New column', beforeId })),
      renameColumn: (id: string, title: string) => run((state) => renameColumn(state, id, title)),
      moveColumn: (id: string, direction: Direction) => run((state) => moveColumn(state, id, direction)),
      setColumnGroup: (id: string, groupId: string | null) => run((state) => setColumnGroup(state, id, groupId)),
      deleteColumn: (id: string) => {
        const state = useBoardStore.getState().entities
        if (!state) return
        const count = countCardsInColumn(state, id)
        confirmThen(
          'Delete column',
          `"${state.columns[id]?.title ?? 'This column'}" and everything in it will be removed.`,
          count > 0 ? `${plural(count, 'card')} will be deleted.` : 'This column has no cards.',
          () => run((current) => deleteColumn(current, id)),
        )
      },

      addGroup: (title: string) => run((state) => createGroup(state, title)),
      renameGroup: (id: string, title: string) => run((state) => renameGroup(state, id, title)),
      deleteGroup: (id: string) => {
        const state = useBoardStore.getState().entities
        if (!state) return
        const members = state.columnIds.filter((columnId) => state.columns[columnId]?.groupId === id).length
        confirmThen(
          'Delete group',
          `"${state.groups[id]?.title ?? 'This group'}" will be removed.`,
          members > 0 ? `${plural(members, 'column')} will be ungrouped. No cards are deleted.` : undefined,
          () => run((current) => deleteGroup(current, id)),
        )
      },

      addSwimlane: () => run((state) => createSwimlane(state, { title: 'New swimlane' })),
      renameSwimlane: (id: string, title: string) => run((state) => renameSwimlane(state, id, title)),
      moveSwimlane: (id: string, direction: Direction) => run((state) => moveSwimlane(state, id, direction)),
      deleteSwimlane: (id: string) => {
        const state = useBoardStore.getState().entities
        if (!state) return
        const count = countCardsInSwimlane(state, id)
        const rows = (state.rowIdsBySwimlane[id] ?? []).length
        confirmThen(
          'Delete swimlane',
          `"${state.swimlanes[id]?.title ?? 'This swimlane'}" and its rows will be removed.`,
          `${plural(rows, 'row')} and ${plural(count, 'card')} will be deleted.`,
          () => run((current) => deleteSwimlane(current, id)),
        )
      },

      addRow: (swimlaneId: string, beforeId: string | null = null) =>
        run((state) => createRow(state, { swimlaneId, title: 'New row', beforeId })),
      renameRow: (id: string, title: string) => run((state) => renameRow(state, id, title)),
      moveRow: (id: string, direction: Direction) => run((state) => moveRow(state, id, direction)),
      moveRowToSwimlane: (id: string, swimlaneId: string) =>
        run((state) => moveRowToSwimlane(state, id, swimlaneId)),
      deleteRow: (id: string) => {
        const state = useBoardStore.getState().entities
        if (!state) return
        const count = countCardsInRow(state, id)
        confirmThen(
          'Delete row',
          `"${state.rows[id]?.title ?? 'This row'}" and everything in it will be removed.`,
          count > 0 ? `${plural(count, 'card')} will be deleted.` : 'This row has no cards.',
          () => run((current) => deleteRow(current, id)),
        )
      },

      openCard: (cardId: string) => useUiStore.getState().openEditor({ mode: 'edit', cardId }),
      moveCard: (cardId: string) => useUiStore.getState().openMove(cardId),
      promptCard: (cardId: string) => useUiStore.getState().openPrompt(cardId),
      addCard: (rowId: string, columnId: string) => {
        const ui = useUiStore.getState()
        if (!ui.quickInsert) {
          ui.openEditor({ mode: 'create', rowId, columnId })
          return
        }
        // Quick insert: the card lands on the board and its title opens for editing.
        // The domain rejects an empty title, so it is created named and pre-selected.
        const store = useBoardStore.getState()
        store.run((state) => createCard(state, { rowId, columnId, draft: { title: 'Untitled' } }))
        // `run` returns nothing, and createCard appends with no anchor, so the new card
        // is the last id in the cell.
        const ids = useBoardStore.getState().entities?.cardIdsByCell[cellKey(rowId, columnId)]
        const newId = ids?.[ids.length - 1]
        if (newId) ui.setFocusCardId(newId)
      },
    }
  }, [])
}

export type StoryMapActions = ReturnType<typeof useStoryMapActions>
